import React, { useState, useEffect, useRef } from "react";
import { TextInput, SelectInput } from "../../Components/Inputs";
import init_shipment from "../init_shipment";
import Btn from "../../../../Components/Buttons/Btn";
import { useNavigate } from "react-router-dom";
import useAxios from "../../../../Hooks/useAxios";

const Add = () => {
  const [input, setInput] = useState({ ...init_shipment });
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const request = useAxios();

  // Use ref to prevent infinite loops
  const hasFetched = useRef(false);

  // Fetch all lists on mount - RUN ONCE
  useEffect(() => {
    if (hasFetched.current) return;

    const fetchData = async () => {
      hasFetched.current = true;
      setLoading(true);
      setError(null);

      try {
        console.log("Fetching data for shipment form...");

        // Fetch customers
        const customerRes = await request({
          method: "GET",
          url: "customers/all",
          show_error: false,
        });
        console.log("Customers response:", customerRes);

        if (customerRes && customerRes.docs) {
          setCustomers(customerRes.docs);
        } else {
          console.warn("No customers found");
        }

        // Fetch vehicles
        const vehicleRes = await request({
          method: "GET",
          url: "vehicles/all-vehicles",
          show_error: false,
        });
        console.log("Vehicles response:", vehicleRes);

        if (vehicleRes && vehicleRes.docs) {
          setVehicles(vehicleRes.docs);
        } else {
          console.warn("No vehicles found");
        }

        // Fetch all members
        const membersRes = await request({
          method: "GET",
          url: "members/all",
          show_error: false,
        });
        console.log("Members response:", membersRes);

        if (membersRes && membersRes.docs) {
          // Filter drivers
          const driversList = membersRes.docs.filter(member =>
            member.role_id === 5 ||
            (member.role && member.role.toLowerCase().includes('driver'))
          );
          setDrivers(driversList);

          // Filter dispatchers
          const dispatchersList = membersRes.docs.filter(member =>
            member.role_id === 2 ||
            (member.role && member.role.toLowerCase().includes('dispatcher'))
          );
          setDispatchers(dispatchersList);
        } else {
          console.warn("No members found");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load form data. Please check your connection.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      hasFetched.current = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log("Form submission - current input state:", input);
      
      // Ensure we're working with strings, not objects
      const customerId = typeof input.customer_id === 'object' ? input.customer_id.value || "" : input.customer_id || "";
      const origin = typeof input.origin === 'object' ? input.origin.value || "" : input.origin || "";
      const destination = typeof input.destination === 'object' ? input.destination.value || "" : input.destination || "";
      
      // Debug: Log the exact value of customer_id
      console.log("customer_id value:", customerId, "Type:", typeof customerId);
      console.log("origin value:", origin, "Type:", typeof origin);
      console.log("destination value:", destination, "Type:", typeof destination);
      
      // Validate required fields
      if (!customerId || customerId === "" || customerId === "0") {
        throw new Error("Please select a customer");
      }
      if (!origin || origin.trim() === "") {
        throw new Error("Please enter origin");
      }
      if (!destination || destination.trim() === "") {
        throw new Error("Please enter destination");
      }

      // Parse values - handle object or string
      const totalPriceValue = typeof input.total_price === 'object' ? input.total_price.value || "0" : input.total_price || "0";
      const parsedTotalPrice = parseFloat(totalPriceValue) || 0;

      // Create items array
      const itemsArray = [{
        goods_type: "General Goods",
        quantity: 1,
        unit: "shipment",
        price_per_unit: parsedTotalPrice,
        total_price: parsedTotalPrice
      }];

      // Get vehicle, driver, dispatcher IDs - handle objects
      const vehicleId = typeof input.vehicle_id === 'object' ? input.vehicle_id.value || "" : input.vehicle_id || "";
      const driverId = typeof input.driver_id === 'object' ? input.driver_id.value || "" : input.driver_id || "";
      const dispatcherId = typeof input.dispatcher_id === 'object' ? input.dispatcher_id.value || "" : input.dispatcher_id || "";

      // Build payload
      const payload = {
        customer_id: parseInt(customerId),
        vehicle_id: vehicleId && vehicleId !== "" ? parseInt(vehicleId) : null,
        driver_id: driverId && driverId !== "" ? parseInt(driverId) : null,
        dispatcher_id: dispatcherId && dispatcherId !== "" ? parseInt(dispatcherId) : null,
        origin: origin.trim(),
        destination: destination.trim(),
        deposit_amount: 0,
        status: "PENDING",
        items: itemsArray
      };

      console.log("Sending shipment payload:", payload);

      // Make API request
      const res = await request({
        method: "POST",
        url: "shipments/add",
        body: payload,
        show_error: false, // Add this to handle errors manually
      });

      console.log("Shipment API response:", res);

      if (res && res.status === "error") {
        throw new Error(res.message || "Failed to create shipment");
      }

      if (res && (res.shipment || res.id || res.success)) {
        const successMessage = res.server_message || res.message || "✓ Shipment created successfully!";
        setSuccess(successMessage);
        
        // Show success message for 3 seconds then navigate
        setTimeout(() => {
          navigate("/dashboard/shipments");
        }, 3000);
        
      } else if (res && res.error) {
        throw new Error(res.error.message || res.error);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating shipment:", error);
      setError(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Manual validation function
  const handleValidation = () => {
    // Clear previous messages
    setError(null);
    setSuccess(null);
    
    // Ensure we're working with strings, not objects
    const customerId = typeof input.customer_id === 'object' ? input.customer_id.value || "" : input.customer_id || "";
    const origin = typeof input.origin === 'object' ? input.origin.value || "" : input.origin || "";
    const destination = typeof input.destination === 'object' ? input.destination.value || "" : input.destination || "";
    
    // First, manually validate
    if (!customerId || customerId === "") {
      setError("Please select a customer");
      const customerSelect = document.querySelector('[name="customer_id"]');
      if (customerSelect) {
        customerSelect.focus();
        customerSelect.style.borderColor = "#ef4444";
      }
      return;
    }
    
    if (!origin || origin.trim() === "") {
      setError("Please enter origin");
      const originInput = document.querySelector('[name="origin"]');
      if (originInput) {
        originInput.focus();
        originInput.style.borderColor = "#ef4444";
      }
      return;
    }
    
    if (!destination || destination.trim() === "") {
      setError("Please enter destination");
      const destinationInput = document.querySelector('[name="destination"]');
      if (destinationInput) {
        destinationInput.focus();
        destinationInput.style.borderColor = "#ef4444";
      }
      return;
    }
    
    // Call submit
    handleSubmit();
  };

  // Helper function to handle input changes (handles both objects and strings)
  const handleInputChange = (field, value) => {
    // If value is an object with a 'value' property, extract it
    const finalValue = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
    setInput({ ...input, [field]: finalValue });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t shadow-2xl w-[85%] m-auto mt-12 p-5 rounded-md bg-white">
      <h3 className="text-center text-xl font-semibold text-primary mb-6">
        Add New Shipment
      </h3>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <strong className="font-medium">Success!</strong>
          </div>
          <p className="mt-1 ml-7">{success}</p>
          <p className="mt-2 ml-7 text-sm text-green-600">Redirecting to shipments list in 3 seconds...</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <strong className="font-medium">Error:</strong>
          </div>
          <p className="mt-1 ml-7">{error}</p>
        </div>
      )}

      <div className="mt-5">
        {/* Required Fields Section */}
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b">Required Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Origin *
              </label>
              <input
                type="text"
                name="origin"
                value={typeof input.origin === 'object' ? input.origin.value || "" : input.origin || ""}
                onChange={(e) => handleInputChange('origin', e.target.value)}
                placeholder="Enter origin address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination *
              </label>
              <input
                type="text"
                name="destination"
                value={typeof input.destination === 'object' ? input.destination.value || "" : input.destination || ""}
                onChange={(e) => handleInputChange('destination', e.target.value)}
                placeholder="Enter destination address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Price *
              </label>
              <input
                type="number"
                name="total_price"
                value={typeof input.total_price === 'object' ? input.total_price.value || "" : input.total_price || ""}
                onChange={(e) => handleInputChange('total_price', e.target.value)}
                placeholder="Enter total price"
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer *
              </label>
              <select
                name="customer_id"
                value={typeof input.customer_id === 'object' ? input.customer_id.value || "" : input.customer_id || ""}
                onChange={(e) => handleInputChange('customer_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Customer *</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.company_name ? ` — ${c.company_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Optional Fields Section */}
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-600 mb-3 pb-2 border-b">Optional Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle (optional)
              </label>
              <select
                name="vehicle_id"
                value={typeof input.vehicle_id === 'object' ? input.vehicle_id.value || "" : input.vehicle_id || ""}
                onChange={(e) => handleInputChange('vehicle_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Vehicle (optional)</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.registration_no || v.plate_no || "Vehicle"}
                    {v.make ? ` - ${v.make}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver (optional)
              </label>
              <select
                name="driver_id"
                value={typeof input.driver_id === 'object' ? input.driver_id.value || "" : input.driver_id || ""}
                onChange={(e) => handleInputChange('driver_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Driver (optional)</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name || d.full_name}
                    {d.email ? ` (${d.email})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dispatcher (optional)
              </label>
              <select
                name="dispatcher_id"
                value={typeof input.dispatcher_id === 'object' ? input.dispatcher_id.value || "" : input.dispatcher_id || ""}
                onChange={(e) => handleInputChange('dispatcher_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Dispatcher (optional)</option>
                {dispatchers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name || d.full_name}
                    {d.email ? ` (${d.email})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
        <Btn
          onClick={() => navigate("/dashboard/shipments")}
          variant="secondary"
          disabled={formLoading}
        >
          Back
        </Btn>

        <Btn
          onClick={handleValidation}
          disabled={formLoading || loading || success}
          className={success ? "opacity-50 cursor-not-allowed" : ""}
        >
          {formLoading ? "Creating..." : success ? "Created Successfully!" : "Submit"}
        </Btn>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        <p>* Required fields</p>
        <p className="mt-1">Note: System will automatically create shipment items.</p>
      </div>
    </div>
  );
};

export default Add;